import React, { useContext, useEffect, useState, useRef } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    useToast,
    Badge,
    HStack,
    Icon,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Grid,
    Spinner,
    Progress,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@chakra-ui/react';
import { FiUploadCloud, FiFile, FiTrash2, FiDownload, FiCheckCircle, FiClock, FiBarChart2, FiUsers, FiLayers } from 'react-icons/fi';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Loading from '../compnents/Loading';
import { useNavigate } from 'react-router-dom';
import UploadYourFile from './UploadYourFile';
import LiveJobIdLoader from '../compnents/LiveJobIdLoader';
import JobProgressContext from '../context/JobProgressContext';
import ConfirmDialog from '../compnents/ConfirmDialog';
import EmptyStateCard from '../compnents/EmptyStateCard';
import FirmReadyDatasets from '../compnents/FirmReadyDatasets';
import ImportPreflight from '../compnents/ImportPreflight';
import LatestImportOutcome from '../compnents/LatestImportOutcome';
import templateFile from '../assets/required_fields_template.xlsx';
import exampleFile from '../assets/required_fields_example.xlsx';
import { demoTrialSampleAssetPath, demoTrialSampleCount, demoTrialSampleFileName, demoDatasetSummary, demoSpotlightCustomers } from '../content/demoDataset';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const Datasets = () => {
    const { token, user, setUser, logout, refreshUser, isAuthInitializing } = useContext(AuthContext);
    const { jobStatus, showLiveLoader, triggerJobCheck } = useContext(JobProgressContext);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [previewData, setPreviewData] = useState({});
    const [loadingPreview, setLoadingPreview] = useState({});
    const [deletingFileId, setDeletingFileId] = useState(null);
    const [filePendingDelete, setFilePendingDelete] = useState(null);
    // const [jobsStatus, setJobsStatus] = useState({ loading: true, hasIncompleteJobs: false });

    // Upload states
    const [uploadFile, setUploadFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [isLoadingDemoDataset, setIsLoadingDemoDataset] = useState(false);

    const [preflight, setPreflight] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const preflightRequest = useRef(0);
    const fileInputRef = useRef(null);
    const progressIntervalRef = useRef(null);
    // const jobCheckIntervalRef = useRef(null);
    // const wasIncompleteRef = useRef(false);
    const toast = useToast();
    const navigate = useNavigate();

    // Fetch all uploaded files
    const fetchFiles = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/content/list-all-uploaded-files`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFiles(res.data.files || []);
        } catch (error) {
            console.error('Error fetching files:', error);
            if (error.response?.status === 420) logout();
            toast({
                title: 'Failed to fetch files',
                description: error.response?.data?.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            return;
        }

        setLoading(true);
        fetchFiles();
        // eslint-disable-next-line
    }, [token]);

    // Cleanup progress interval on unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);

    if (isAuthInitializing || !token) {
        return (
            <Loading />
        );
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    const hasDatasetUploadAccess = ['solo', 'pro', 'business'].includes(user?.subscription);

    if (user && user.noOfFileUploads === 0 && !hasDatasetUploadAccess) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Container maxW="container.xl" flex="1" py={8} px={{ base: 4, md: 6 }}>
                        <VStack align="stretch" spacing={6}>
                            <EmptyStateCard
                                icon={FiUploadCloud}
                                title="Start a trial before uploading client data"
                                description="The demo workspace is available without payment. Uploading a real CSV or Excel customer file requires Solo, Team or Business, or a 30-day trial."
                                hint="Use the demo dashboard to evaluate the product, then activate the trial when you are ready to process your own dataset."
                                bg="white"
                                borderColor="brand.divider"
                                titleColor="gray.800"
                                descriptionColor="gray.600"
                                hintColor="gray.500"
                                iconColor="brand.teal"
                                maxW="3xl"
                            >
                                <Button colorScheme="blue" onClick={() => navigate('/subscription?trial=pro')}>
                                    Start a 30-day trial
                                </Button>
                                <Button variant="outline" colorScheme="blue" onClick={() => navigate('/dashboard')}>
                                    View demo dashboard
                                </Button>
                                <Button
                                    as="a"
                                    href={demoTrialSampleAssetPath}
                                    download={demoTrialSampleFileName}
                                    variant="ghost"
                                    colorScheme="teal"
                                    leftIcon={<FiDownload />}
                                >
                                    Import sample ({demoTrialSampleCount} clients)
                                </Button>
                            </EmptyStateCard>
                        </VStack>
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        // First-time upload flow + sidebar so firm-ready datasets (product catalog,
        // holdings, transactions, evidence, model targets) stay reachable even
        // before the first customer CSV is processed.
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Box flex="1" display="flex" flexDirection="column">
                        <UploadYourFile setUser={setUser} token={token} />
                        <Container maxW="container.xl" pt={4} pb={10} px={{ base: 4, md: 6 }} w="75vw" marginLeft={{ base: 10, md: 16 }}>
                            <FirmReadyDatasets />
                        </Container>
                    </Box>
                </Box>
                <Footer />
            </Box>
        )
    }

    // Fetch preview for a specific file
    const fetchPreview = async (fileId) => {
        if (previewData[fileId]) return; // Already loaded

        setLoadingPreview(prev => ({ ...prev, [fileId]: true }));
        try {
            const res = await axios.get(`${API_BASE_URL}/content/preview-file/${fileId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPreviewData(prev => ({ ...prev, [fileId]: res.data }));
        } catch (error) {
            console.error('Error previewing file:', error);
            toast({
                title: 'Preview failed',
                description: error.response?.data?.message || 'Could not load preview.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoadingPreview(prev => ({ ...prev, [fileId]: false }));
        }
    };

    // When tab changes, fetch preview if needed
    const handleTabChange = (index) => {
        setActiveTab(index);
        if (index > 0 && index <= files.length) {
            const file = files[index - 1];
            fetchPreview(file.file_id);
        }
    };

    // Upload file handler
    const handleFileUpload = async (event) => {
        if (!hasDatasetUploadAccess) {
            toast({
                title: 'Start a trial before uploading',
                description: 'The demo is available now. Real customer datasets require Solo, Team or Business, or a 30-day trial.',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            navigate('/subscription?trial=pro');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        const selectedFiles = Array.from(event.target.files);
        const validFiles = selectedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls')
        );

        if (validFiles.length !== selectedFiles.length) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload only CSV or Excel files',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setUploadFile(fileToUpload);
            await checkFileBeforeImport(fileToUpload);
        }
    };

    // Every client extract is checked before anything is saved: same report,
    // column mapping and plan-capacity warning as the first upload.
    const checkFileBeforeImport = async (file, mapping = null) => {
        const request = ++preflightRequest.current;
        if (!mapping) setPreflight(null);
        setIsChecking(true);
        setUploadError(null);
        try {
            const form = new FormData();
            form.append('file', file);
            if (mapping) form.append('mapping', JSON.stringify(mapping));
            const response = await axios.post(`${API_BASE_URL}/content/preflight-file`, form, {
                headers: { Authorization: `Bearer ${token}` }, timeout: 65000,
            });
            if (request === preflightRequest.current) setPreflight({ file, report: response.data });
        } catch (error) {
            if (request === preflightRequest.current) {
                setUploadError(error.response?.data?.message || 'Unable to check this file. Nothing has been imported.');
            }
        } finally {
            if (request === preflightRequest.current) setIsChecking(false);
        }
    };

    const cancelPreflight = () => {
        preflightRequest.current += 1;
        setPreflight(null);
        setUploadFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Start artificial progress simulation
    const startProgressSimulation = () => {
        // Clear any existing interval
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }

        // Start at current progress or 10% if just starting
        let currentProgress = uploadProgress || 10;
        setUploadProgress(currentProgress);

        // Increment progress gradually
        progressIntervalRef.current = setInterval(() => {
            setUploadProgress((prev) => {
                // Increment by 2-3% randomly
                const increment = Math.random() * 1 + 2; // 2-3%
                const newProgress = prev + increment;

                // Cap at 95% to leave room for completion
                if (newProgress >= 95) {
                    if (progressIntervalRef.current) {
                        clearInterval(progressIntervalRef.current);
                    }
                    return 95;
                }

                return newProgress;
            });
        }, 800); // Update every 800ms
    };

    // Complete the progress bar
    const completeProgress = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }
        setUploadProgress(100);
    };

    // Upload to API
    const uploadFileToAPI = async (file, mapping = null) => {
        const formData = new FormData();
        formData.append('file', file);
        // Import exactly the mapping that the preflight checked.
        if (mapping && Object.keys(mapping).length) formData.append('mapping', JSON.stringify(mapping));

        try {
            setIsUploading(true);
            setUploadProgress(0);
            setUploadError(null);

            const response = await axios.post(`${API_BASE_URL}/content/upload-file`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                },
                onUploadProgress: (progressEvent) => {
                    // This tracks the actual file upload (sending bytes)
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );

                    // Only use this for the initial upload phase (0-10%)
                    if (percentCompleted < 100) {
                        setUploadProgress(Math.min(percentCompleted * 0.1, 10));
                    } else {
                        // Once upload is complete, start the artificial progress
                        startProgressSimulation();
                    }
                }
            });

            // Clear the interval and complete the progress
            completeProgress();

            const data = response.data;

            if (data.status === 'failed' && data.preview?.error) {
                setUploadError(data.preview.error);
                toast({
                    title: 'Upload validation failed',
                    description: data.preview.error,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                // Follow the job whether or not the backend could render preview rows:
                // the job result carries accepted/rejected rows and the plan-capacity outcome.
                toast({
                    title: 'File uploaded successfully',
                    description: 'Processing your dataset...',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });

                await refreshUser();
                await fetchFiles();
                await triggerJobCheck();
            }
            return data;
        } catch (error) {
            console.error('Upload error:', error);

            // Clear the interval on error
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }

            // const errorMessage = error.response?.data?.message ||
            //     error.response?.data?.error ||
            //     'Failed to upload file. Please try again.';

            // setUploadError(errorMessage);
            // toast({
            //     title: 'Upload failed',
            //     description: errorMessage,
            //     status: 'error',
            //     duration: 5000,
            //     isClosable: true,
            // });

            // Check for 413 Content Too Large status
            if (error.response?.status === 413) {
                const contentTooLargeMessage = 'Content too large. Max Limit 25MB.';
                setUploadError(contentTooLargeMessage);
                toast({
                    title: 'Upload failed',
                    description: contentTooLargeMessage,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                const errorMessage = error.response?.data?.message ||
                    error.response?.data?.error ||
                    'Failed to upload file. Please try again.';

                setUploadError(errorMessage);
                toast({
                    title: 'Upload failed',
                    description: errorMessage,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } finally {
            setIsUploading(false);
            setUploadFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Delete a file
    const handleDelete = async () => {
        if (!filePendingDelete) {
            return;
        }

        const fileId = filePendingDelete;
        setDeletingFileId(fileId);
        try {
            const response = await axios.get(`${API_BASE_URL}/content/delete-file/${fileId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast({
                title: 'File deleted',
                description: response.data?.message,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Remove from preview data
            setPreviewData(prev => {
                const newData = { ...prev };
                delete newData[fileId];
                return newData;
            });

            // Refresh files and go to upload tab
            await fetchFiles();
            setActiveTab(0);
        } catch (error) {
            console.error('Error deleting file:', error);
            toast({
                title: 'Delete failed',
                description: error.response?.data?.message || 'Try again later.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setDeletingFileId(null);
            setFilePendingDelete(null);
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleDownloadFile = (fileUrl, fileName) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleLoadDemoDataset = async () => {
        if (!hasDatasetUploadAccess) {
            toast({
                title: 'Demo dashboard is already available',
                description: 'Open Dashboard to inspect the synthetic workspace without consuming an upload.',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            navigate('/dashboard');
            return;
        }

        try {
            setIsLoadingDemoDataset(true);
            setUploadError(null);

            const response = await fetch(demoTrialSampleAssetPath);
            if (!response.ok) {
                throw new Error('Unable to fetch demo dataset');
            }

            const blob = await response.blob();
            const demoFile = new File(
                [blob],
                demoTrialSampleFileName,
                {
                    type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }
            );

            setUploadFile(demoFile);
            setActiveTab(0);
            await uploadFileToAPI(demoFile);
        } catch (error) {
            console.error('Error loading demo dataset:', error);
            toast({
                title: 'Demo dataset unavailable',
                description: 'We could not preload the synthetic customer dataset. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoadingDemoDataset(false);
        }
    };

    const completedFiles = files.filter((file) => file.job_status === 'completed');
    const failedFiles = files.filter((file) => file.job_status === 'failed');
    const processingFiles = files.filter((file) => ['processing', 'queued', 'running'].includes(file.job_status));
    const hasResultsReady = completedFiles.length > 0;
    const hasDatasetHistory = files.length > 0;
    const hasActiveProcessing = showLiveLoader || jobStatus.hasIncompleteJobs || processingFiles.length > 0;
    const latestDataset = files[0] || null;
    const latestReadyDataset = completedFiles[0] || latestDataset;
    // Solo has one person and no RM assignment; Team has Admin and RM views only.
    const isSoloPlan = user?.subscription === 'solo';
    const activationChecklist = [
        isSoloPlan ? {
            id: 'dashboards',
            title: 'Open your review queue',
            description: 'See which of your clients to review first and the evidence behind each case.',
            cta: 'Open my reviews',
            path: '/dashboard/rm?view=who-to-call',
            icon: FiBarChart2,
        } : {
            id: 'dashboards',
            title: 'Review role dashboards',
            description: user?.subscription === 'pro'
                ? 'Validate the Admin and Relationship Manager views from the same dataset.'
                : 'Validate Admin, RM, Compliance, Head, and Portfolio Adviser views from the same dataset.',
            cta: 'Open dashboards',
            path: '/dashboard',
            icon: FiBarChart2,
        },
        !isSoloPlan && {
            id: 'assignments',
            title: 'Assign customers to RMs',
            description: 'Make ownership visible before asking teams to use priority queues and who-to-call lists.',
            cta: 'Assign customers',
            path: '/settings/manageCustomers',
            icon: FiUsers,
        },
        {
            id: 'reports',
            title: 'Generate first report',
            description: 'Open exports for an executive snapshot, governance readout, or follow-up pack.',
            cta: 'Open reports',
            path: '/reportsExports',
            icon: FiDownload,
        },
        {
            id: 'trial',
            title: hasDatasetUploadAccess ? 'Review plan access' : 'Start a 30-day trial',
            description: hasDatasetUploadAccess
                ? 'Confirm the plan before rolling this workflow out to the broader team.'
                : 'Keep the demo open, then activate trial access when you are ready to process real client data.',
            cta: hasDatasetUploadAccess ? 'Open subscription' : 'Start trial',
            path: hasDatasetUploadAccess ? '/subscription' : '/subscription?trial=pro',
            icon: FiCheckCircle,
        },
    ].filter(Boolean);
    const journeySteps = [
        {
            id: 'prepare',
            title: 'Prepare your file',
            description: 'Start from the template or example file and make sure the core fields are present.',
            icon: FiUploadCloud,
            status: hasDatasetHistory ? 'complete' : 'current',
        },
        {
            id: 'processing',
            title: 'Validate and process',
            description: 'FinanSee checks the structure, stores the dataset, and launches the scoring workflow.',
            icon: FiClock,
            status: hasResultsReady ? 'complete' : hasActiveProcessing ? 'current' : hasDatasetHistory ? 'current' : 'upcoming',
        },
        {
            id: 'results',
            title: 'Activate next actions',
            description: 'Move to dashboards, reports, and RM assignment once the dataset is ready.',
            icon: FiBarChart2,
            status: hasResultsReady ? 'complete' : 'upcoming',
        },
    ];

    const getStepAccent = (status) => {
        if (status === 'complete') {
            return {
                borderColor: 'green.200',
                bg: 'green.50',
                badge: 'green',
                label: 'Complete',
            };
        }

        if (status === 'current') {
            return {
                borderColor: 'teal.100',
                bg: 'brand.tealSoft',
                badge: 'blue',
                label: 'Current',
            };
        }

        return {
            borderColor: 'gray.200',
            bg: 'white',
            badge: 'gray',
            label: 'Next',
        };
    };

    // Format value for display
    // const formatValue = (value) => {
    //     if (value === null || value === undefined) return '-';
    //     if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    //     if (typeof value === 'number') return value.toLocaleString();
    //     return value.toString();
    // };

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        if (!hasDatasetUploadAccess) {
            toast({
                title: 'Start a trial before uploading',
                description: 'The demo is available now. Real customer datasets require Solo, Team or Business, or a 30-day trial.',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            navigate('/subscription?trial=pro');
            return;
        }

        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = droppedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls')
        );

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setUploadFile(fileToUpload);
            await checkFileBeforeImport(fileToUpload);
        }
    };

    if (loading) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Container maxW="container.xl" flex="1">
                        <Loading />
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    //NEW CHECK - Show loading while checking job status
    if (jobStatus.loading) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Container maxW="container.xl" flex="1">
                        <Loading />
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    if (showLiveLoader || jobStatus.hasIncompleteJobs) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Box flex="1" display="flex" flexDirection="column">
                        <Container maxW="container.xl" pt={8} px={{ base: 4, md: 6 }}>
                            <VStack align="start" spacing={2} mb={4}>
                                <Heading as="h1" size="lg" color="gray.800" fontWeight="semibold">
                                    Processing Your Dataset
                                </Heading>
                                <Text color="gray.600" fontSize="sm">
                                    Please wait while we process your data. This page will automatically update.
                                </Text>
                            </VStack>
                        </Container>
                        <LiveJobIdLoader />
                    </Box>
                </Box>
                <Footer />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />

                <Container maxW="container.xl" flex="1" py={8} px={{ base: 4, md: 6 }} w="75vw" marginLeft={{ base: 10, md: 16 }}>
                    <VStack align="start" spacing={6} w="90%">
                        <Box
                            w="full"
                            bg="linear-gradient(135deg, rgba(30,136,229,0.12) 0%, rgba(232,244,255,1) 55%, rgba(255,255,255,1) 100%)"
                            borderRadius="2xl"
                            border="1px solid"
                            borderColor="brand.divider"
                            px={{ base: 5, md: 8 }}
                            py={{ base: 6, md: 8 }}
                            boxShadow="sm"
                        >
                            <Grid templateColumns={{ base: '1fr', xl: '1.35fr 0.85fr' }} gap={6}>
                                <Box>
                                    <Badge colorScheme="blue" borderRadius="full" px={3} py={1} mb={4}>
                                        Guided onboarding
                                    </Badge>
                                    <Heading as="h1" size="lg" color="gray.800" fontWeight="semibold" mb={3}>
                                        Upload, validate, and activate your customer base in one clear flow
                                    </Heading>
                                    <Text color="gray.600" maxW="3xl" fontSize="md">
                                        This workspace is the operational entry point for Playground. Start from a clean template,
                                        upload one CSV or Excel file, let FinanSee process it, and then move directly into dashboards,
                                        reports, and RM assignment.
                                    </Text>
                                </Box>
                                <Box
                                    bg="white"
                                    borderRadius="xl"
                                    border="1px solid"
                                    borderColor="brand.divider"
                                    p={5}
                                    boxShadow="sm"
                                >
                                    <Text fontSize="xs" fontWeight="bold" color="brand.navy2" textTransform="uppercase" mb={3}>
                                        Workspace status
                                    </Text>
                                    <VStack align="stretch" spacing={3}>
                                        <HStack justify="space-between">
                                            <Text color="gray.600" fontSize="sm">Datasets uploaded</Text>
                                            <Badge colorScheme="blue" fontSize="sm">{files.length}</Badge>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text color="gray.600" fontSize="sm">Results ready</Text>
                                            <Badge colorScheme={hasResultsReady ? 'green' : 'gray'} fontSize="sm">
                                                {hasResultsReady ? completedFiles.length : 'Not yet'}
                                            </Badge>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text color="gray.600" fontSize="sm">Processing jobs</Text>
                                            <Badge colorScheme={hasActiveProcessing ? 'yellow' : 'gray'} fontSize="sm">
                                                {hasActiveProcessing ? processingFiles.length || 1 : 0}
                                            </Badge>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text color="gray.600" fontSize="sm">Validation failures</Text>
                                            <Badge colorScheme={failedFiles.length > 0 ? 'red' : 'gray'} fontSize="sm">
                                                {failedFiles.length}
                                            </Badge>
                                        </HStack>
                                    </VStack>
                                </Box>
                            </Grid>
                        </Box>

                        <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={4} w="full">
                            {journeySteps.map((step, index) => {
                                const accent = getStepAccent(step.status);

                                return (
                                    <Box
                                        key={step.id}
                                        bg={accent.bg}
                                        border="1px solid"
                                        borderColor={accent.borderColor}
                                        borderRadius="xl"
                                        p={5}
                                    >
                                        <HStack justify="space-between" align="start" mb={4}>
                                            <Box
                                                w={11}
                                                h={11}
                                                borderRadius="full"
                                                bg="white"
                                                border="1px solid"
                                                borderColor={accent.borderColor}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Icon as={step.icon} color={accent.badge === 'gray' ? 'gray.500' : `${accent.badge}.500`} boxSize={5} />
                                            </Box>
                                            <Badge colorScheme={accent.badge} borderRadius="full">
                                                {accent.label}
                                            </Badge>
                                        </HStack>
                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
                                            Step {index + 1}
                                        </Text>
                                        <Text fontWeight="semibold" color="gray.800" mb={2}>
                                            {step.title}
                                        </Text>
                                        <Text fontSize="sm" color="gray.600">
                                            {step.description}
                                        </Text>
                                    </Box>
                                );
                            })}
                        </Grid>

                        <Box w="full" bg="white" borderRadius="xl" shadow="sm" border="1px" borderColor="gray.200">
                            <Tabs index={activeTab} onChange={handleTabChange}>
                                <TabList overflowX="auto" overflowY="hidden" flexWrap="nowrap">
                                    {/* Upload Dataset Tab */}
                                    <Tab
                                        fontWeight="semibold"
                                        fontSize="sm"
                                        _selected={{ color: 'brand.navy', borderColor: 'brand.navy' }}
                                        whiteSpace="nowrap"
                                        borderRight="1px solid black"
                                    >
                                        <Icon as={FiUploadCloud} mr={2} />
                                        Upload Dataset
                                    </Tab>

                                    {/* File Tabs */}
                                    {files.map((file) => (
                                        <Tab
                                            key={file.file_id}
                                            fontWeight="medium"
                                            fontSize="sm"
                                            _selected={{ color: 'brand.navy', borderColor: 'brand.navy' }}
                                            maxW="150px"
                                            borderRight="1px solid black"
                                            mr={1}
                                        >
                                            <Icon as={FiFile} mr={2} />
                                            <Text isTruncated>{file.filename}</Text>
                                        </Tab>
                                    ))}

                                    {/* Firm-ready data (product catalog, holdings, transactions, evidence, model targets) */}
                                    <Tab
                                        fontWeight="semibold"
                                        fontSize="sm"
                                        _selected={{ color: 'brand.navy', borderColor: 'brand.navy' }}
                                        whiteSpace="nowrap"
                                    >
                                        <Icon as={FiLayers} mr={2} />
                                        Firm-ready data
                                    </Tab>
                                </TabList>

                                <TabPanels>
                                    {/* Upload Panel */}
                                    {user?.subscription === 'free' ? (
                                        <>
                                            <TabPanel p={6}>
                                                <Box textAlign="center" p={6} border="2px solid" borderColor="gray.200" borderRadius="lg">
                                                    <Text fontSize="lg" mb={4}>
                                                        Uploading customer data requires Solo, Team or Business, or a 30-day trial.
                                                    </Text>
                                                    <HStack justify="center" spacing={3} flexWrap="wrap">
                                                        <Button
                                                            colorScheme="blue"
                                                            size="md"
                                                            onClick={() => navigate('/subscription?trial=pro')}
                                                        >
                                                            Start a 30-day trial
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            colorScheme="blue"
                                                            size="md"
                                                            onClick={() => navigate('/dashboard')}
                                                        >
                                                            View demo dashboard
                                                        </Button>
                                                    </HStack>
                                                </Box>
                                            </TabPanel>
                                        </>
                                    ) : (
                                        <>
                                            <TabPanel p={6}>
                                                <Grid templateColumns={{ base: '1fr', xl: '1.2fr 0.8fr' }} gap={5} mb={6}>
                                                    <Box
                                                        bg="brand.tealSoft"
                                                        borderRadius="xl"
                                                        border="1px solid"
                                                        borderColor="brand.divider"
                                                        p={5}
                                                    >
                                                        <Text fontSize="xs" fontWeight="bold" color="brand.navy2" textTransform="uppercase" mb={2}>
                                                            Step 1: Prepare the source file
                                                        </Text>
                                                        <Heading size="sm" color="gray.800" mb={3}>
                                                            Start from the approved format and upload one clean dataset
                                                        </Heading>
                                                        <Text fontSize="sm" color="gray.600" mb={4}>
                                                            Use the template if you are structuring a file from scratch, or the example workbook if your team
                                                            wants to see the expected format before loading real customers.
                                                        </Text>
                                                        <HStack spacing={3} flexWrap="wrap">
                                                            <Button
                                                                leftIcon={<FiDownload />}
                                                                colorScheme="blue"
                                                                onClick={() => handleDownloadFile(templateFile, 'finansee_customer_template.xlsx')}
                                                            >
                                                                Download template
                                                            </Button>
                                                            <Button
                                                                leftIcon={<FiDownload />}
                                                                variant="outline"
                                                                borderColor="brand.teal"
                                                                color="brand.navy"
                                                                _hover={{ bg: 'brand.divider' }}
                                                                onClick={() => handleDownloadFile(exampleFile, 'finansee_customer_example.xlsx')}
                                                            >
                                                                Download example
                                                            </Button>
                                                        </HStack>
                                                    </Box>
                                                    <Box
                                                        bg="white"
                                                        borderRadius="xl"
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        p={5}
                                                    >
                                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
                                                            Upload checklist
                                                        </Text>
                                                        <VStack align="stretch" spacing={3}>
                                                            <HStack align="start">
                                                                <Icon as={FiCheckCircle} color="green.500" mt={0.5} />
                                                                <Text fontSize="sm" color="gray.600">Accepted formats: `.csv`, `.xlsx`, `.xls`</Text>
                                                            </HStack>
                                                            <HStack align="start">
                                                                <Icon as={FiCheckCircle} color="green.500" mt={0.5} />
                                                                <Text fontSize="sm" color="gray.600">Include the 9 core required fields before upload</Text>
                                                            </HStack>
                                                            <HStack align="start">
                                                                <Icon as={FiCheckCircle} color="green.500" mt={0.5} />
                                                                <Text fontSize="sm" color="gray.600">Optional behavioral fields improve insight quality but are not mandatory</Text>
                                                            </HStack>
                                                            <HStack align="start">
                                                                <Icon as={FiCheckCircle} color="green.500" mt={0.5} />
                                                                <Text fontSize="sm" color="gray.600">Maximum upload size currently supported: 25 MB</Text>
                                                            </HStack>
                                                        </VStack>
                                                    </Box>
                                                </Grid>

                                                <Box
                                                    bg="linear-gradient(135deg, rgba(15,118,110,0.08) 0%, rgba(240,253,250,1) 52%, rgba(255,255,255,1) 100%)"
                                                    borderRadius="xl"
                                                    border="1px solid"
                                                    borderColor="teal.100"
                                                    p={5}
                                                    mb={6}
                                                >
                                                    <Grid templateColumns={{ base: '1fr', xl: '1.1fr 0.9fr' }} gap={5}>
                                                        <Box>
                                                            <Badge colorScheme="teal" borderRadius="full" px={3} py={1} mb={3}>
                                                                Demo mode
                                                            </Badge>
                                                            <Heading size="sm" color="gray.800" mb={3}>
                                                                Load a buyer-ready synthetic dataset in one click
                                                            </Heading>
                                                            <Text fontSize="sm" color="gray.600" mb={4} maxW="2xl">
                                                                This workbook is built from reconstructed synthetic customers and is ready to run through the
                                                                normal Playground upload flow. It is designed for sales calls, onboarding rehearsals, and internal demos.
                                                            </Text>
                                                            <HStack spacing={3} flexWrap="wrap" mb={4}>
                                                                <Badge colorScheme="teal" fontSize="sm">{demoTrialSampleCount} synthetic clients</Badge>
                                                                <Badge colorScheme="blue" fontSize="sm">{demoDatasetSummary.columnsCount} fields</Badge>
                                                            </HStack>
                                                            <HStack spacing={3} flexWrap="wrap">
                                                                <Button
                                                                    colorScheme="teal"
                                                                    onClick={handleLoadDemoDataset}
                                                                    isLoading={isLoadingDemoDataset || isUploading}
                                                                    loadingText="Loading demo dataset..."
                                                                >
                                                                    Load demo dataset
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    colorScheme="teal"
                                                                    onClick={() => handleDownloadFile(demoTrialSampleAssetPath, demoTrialSampleFileName)}
                                                                >
                                                                    Download demo file
                                                                </Button>
                                                            </HStack>
                                                        </Box>
                                                        <Box
                                                            bg="white"
                                                            borderRadius="xl"
                                                            border="1px solid"
                                                            borderColor="teal.100"
                                                            p={4}
                                                        >
                                                            <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
                                                                Full illustrative workspace
                                                            </Text>
                                                            <Text fontSize="sm" color="gray.600" mb={3}>
                                                                These workspace examples are separate from the {demoTrialSampleCount}-client import sample.
                                                            </Text>
                                                            <VStack align="stretch" spacing={3}>
                                                                <HStack justify="space-between">
                                                                    <Text fontSize="sm" color="gray.600">Average age</Text>
                                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">{demoDatasetSummary.avgAge}</Text>
                                                                </HStack>
                                                                <HStack justify="space-between">
                                                                    <Text fontSize="sm" color="gray.600">Coverage</Text>
                                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">{demoDatasetSummary.topCountries.join(', ')}</Text>
                                                                </HStack>
                                                                <HStack align="start">
                                                                    <Icon as={FiUsers} color="teal.500" mt={0.5} />
                                                                    <Text fontSize="sm" color="gray.600">
                                                                        Use the spotlight profiles below to show how buyer conversations can move from file upload
                                                                        to RM prioritization and customer-level action.
                                                                    </Text>
                                                                </HStack>
                                                            </VStack>
                                                        </Box>
                                                    </Grid>
                                                </Box>

                                                <Box mb={6}>
                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                                                        Full workspace spotlight
                                                    </Text>
                                                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={4}>
                                                        {demoSpotlightCustomers.map((customer) => (
                                                            <Box
                                                                key={customer.customerId}
                                                                bg="white"
                                                                border="1px solid"
                                                                borderColor="gray.200"
                                                                borderRadius="xl"
                                                                p={4}
                                                                boxShadow="sm"
                                                            >
                                                                <Text fontSize="xs" fontWeight="bold" color="teal.600" textTransform="uppercase" mb={2}>
                                                                    {customer.customerId} · {customer.country}
                                                                </Text>
                                                                <Text fontWeight="semibold" color="gray.800" mb={2}>
                                                                    {customer.title}
                                                                </Text>
                                                                <Text fontSize="sm" color="gray.600" mb={3}>
                                                                    {customer.angle}
                                                                </Text>
                                                                <VStack align="stretch" spacing={2}>
                                                                    {customer.metrics.map((metric) => (
                                                                        <Badge key={metric} colorScheme="gray" borderRadius="full" px={3} py={1}>
                                                                            {metric}
                                                                        </Badge>
                                                                    ))}
                                                                </VStack>
                                                            </Box>
                                                        ))}
                                                    </Grid>
                                                </Box>

                                                {/* Upload Area */}
                                                <Box
                                                    border="2px dashed"
                                                    borderColor={isUploading ? 'brand.teal' : 'gray.300'}
                                                    borderRadius="lg"
                                                    p={12}
                                                    textAlign="center"
                                                    onDragOver={handleDragOver}
                                                    onDrop={handleDrop}
                                                    mb={4}
                                                    cursor={isUploading ? 'not-allowed' : 'pointer'}
                                                    _hover={{ borderColor: 'brand.teal', bg: 'gray.50' }}
                                                    onClick={() => !isUploading && fileInputRef.current?.click()}
                                                    opacity={isUploading ? 0.6 : 1}
                                                    pointerEvents={isUploading ? 'none' : 'auto'}
                                                >
                                                    {isUploading ? (
                                                        <Spinner size="xl" color="brand.teal" thickness="4px" mb={4} />
                                                    ) : (
                                                        <Icon as={FiUploadCloud} boxSize={10} color="gray.400" mb={4} />
                                                    )}
                                                    <Text fontSize="md" mb={2}>
                                                        {isUploading ? 'Uploading and Processing...' : 'Drag & drop your CSV/XLSX here'}
                                                    </Text>
                                                    {!isUploading && (
                                                        <>
                                                            <Text fontSize="sm" color="gray.500" mb={4}>
                                                                or
                                                            </Text>
                                                            <Button colorScheme="blue" size="md">
                                                                Browse files
                                                            </Button>
                                                            <Text fontSize="xs" color="gray.500" mt={3}>
                                                                Accepted: .csv, .xlsx
                                                            </Text>
                                                        </>
                                                    )}
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept=".csv,.xlsx,.xls"
                                                        onChange={handleFileUpload}
                                                        style={{ display: 'none' }}
                                                        disabled={isUploading || isChecking}
                                                    />
                                                </Box>

                                                {/* Uploaded File Info */}
                                                {uploadFile && (
                                                    <HStack
                                                        bg="gray.50"
                                                        px={3}
                                                        py={2}
                                                        borderRadius="md"
                                                        spacing={2}
                                                        mb={4}
                                                    >
                                                        <Icon as={FiFile} color="gray.600" />
                                                        <Text fontSize="sm" fontWeight="medium">
                                                            {uploadFile.name}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.500">
                                                            • {(uploadFile.size / 1024).toFixed(0)}KB
                                                        </Text>
                                                    </HStack>
                                                )}

                                                {/* Upload Progress */}
                                                {isUploading && (
                                                    <Box mb={4}>
                                                        <HStack justify="space-between" mb={2}>
                                                            <Text fontSize="sm" fontWeight="medium">
                                                                {uploadProgress < 10 ? 'Uploading file...' : 'Processing your data...'}
                                                            </Text>
                                                            <Text fontSize="sm" fontWeight="bold" color="brand.navy">
                                                                {Math.round(uploadProgress)}%
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={uploadProgress}
                                                            colorScheme="blue"
                                                            borderRadius="full"
                                                            size="sm"
                                                            hasStripe
                                                            isAnimated
                                                        />
                                                        <Text fontSize="xs" color="gray.600" mt={2}>
                                                            {uploadProgress < 10
                                                                ? 'Uploading your file to the server...'
                                                                : 'Analyzing and validating your data... This may take up to a minute.'}
                                                        </Text>
                                                    </Box>
                                                )}

                                                {/* Error Alert */}
                                                {uploadError && (
                                                    <Alert status="error" borderRadius="md" mb={4}>
                                                        <AlertIcon />
                                                        <AlertDescription fontSize="sm">
                                                            {uploadError}
                                                        </AlertDescription>
                                                    </Alert>
                                                )}

                                                {isChecking && (
                                                    <HStack mb={4}><Spinner /><Text>Checking rows and evidence coverage. No import has started.</Text></HStack>
                                                )}

                                                {preflight && (
                                                    <ImportPreflight
                                                        filename={preflight.file.name}
                                                        report={preflight.report}
                                                        isImporting={isUploading}
                                                        isRechecking={isChecking}
                                                        onRecheck={(mapping) => checkFileBeforeImport(preflight.file, mapping)}
                                                        onSaveMapping={async (name, columns, mapping) => {
                                                            try {
                                                                const { data } = await axios.post(`${API_BASE_URL}/content/import-mappings`, { name, columns, mapping },
                                                                    { headers: { Authorization: `Bearer ${token}` } });
                                                                return data;
                                                            } catch (error) {
                                                                toast({ title: 'Mapping not saved', description: error.response?.data?.message || 'Please try again.', status: 'error', duration: 5000, isClosable: true });
                                                                return null;
                                                            }
                                                        }}
                                                        onCancel={cancelPreflight}
                                                        onConfirm={async (mapping) => {
                                                            try {
                                                                const result = await uploadFileToAPI(preflight.file, mapping);
                                                                if (result && result.status !== 'failed') setPreflight(null);
                                                            } catch (_) { /* uploadFileToAPI shows the error and keeps the checked file. */ }
                                                        }}
                                                    />
                                                )}

                                                {!preflight && !isUploading && <LatestImportOutcome job={jobStatus.job} />}

                                                {hasResultsReady && latestReadyDataset && !isUploading && (
                                                    <Box
                                                        mt={6}
                                                        bg="green.50"
                                                        borderRadius="xl"
                                                        border="1px solid"
                                                        borderColor="green.100"
                                                        p={5}
                                                    >
                                                        <VStack align="stretch" spacing={5}>
                                                            <Box>
                                                                <Badge colorScheme="green" borderRadius="full" px={3} py={1} mb={3}>
                                                                    Step 3: Results ready
                                                                </Badge>
                                                                <Heading size="sm" color="gray.800" mb={2}>
                                                                    Activation checklist for your latest dataset
                                                                </Heading>
                                                                <Text fontSize="sm" color="gray.600" maxW="2xl">
                                                                    Move from upload validation into the operating flow buyers expect to see:
                                                                    dashboards, RM ownership, client action queues, reports, and plan activation.
                                                                </Text>
                                                                <Text fontSize="sm" color="gray.700" mt={3}>
                                                                    Latest ready file: <b>{latestReadyDataset.filename}</b>
                                                                </Text>
                                                            </Box>
                                                            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                                                                {activationChecklist.map((item) => (
                                                                    <Box
                                                                        key={item.id}
                                                                        bg="white"
                                                                        border="1px solid"
                                                                        borderColor="green.100"
                                                                        borderRadius="lg"
                                                                        p={4}
                                                                    >
                                                                        <HStack align="start" spacing={3}>
                                                                            <Box
                                                                                bg="green.100"
                                                                                color="green.700"
                                                                                borderRadius="full"
                                                                                boxSize="34px"
                                                                                display="flex"
                                                                                alignItems="center"
                                                                                justifyContent="center"
                                                                                flexShrink={0}
                                                                            >
                                                                                <Icon as={item.icon} />
                                                                            </Box>
                                                                            <Box flex="1">
                                                                                <Text fontSize="sm" fontWeight="bold" color="gray.800">
                                                                                    {item.title}
                                                                                </Text>
                                                                                <Text fontSize="xs" color="gray.600" mt={1} minH={{ md: '36px' }}>
                                                                                    {item.description}
                                                                                </Text>
                                                                                <Button
                                                                                    size="sm"
                                                                                    mt={3}
                                                                                    colorScheme="blue"
                                                                                    variant={item.id === 'dashboards' ? 'solid' : 'outline'}
                                                                                    onClick={() => navigate(item.path)}
                                                                                >
                                                                                    {item.cta}
                                                                                </Button>
                                                                            </Box>
                                                                        </HStack>
                                                                    </Box>
                                                                ))}
                                                            </Grid>
                                                        </VStack>
                                                    </Box>
                                                )}
                                            </TabPanel>
                                        </>
                                    )}

                                    {/* File Preview Panels */}
                                    {files.map((file) => (
                                        <TabPanel key={file.file_id} p={6}>
                                            {loadingPreview[file.file_id] ? (
                                                <Box textAlign="center" py={10}>
                                                    <Spinner size="xl" color="brand.teal" mb={4} />
                                                    <Text color="gray.600">Loading preview...</Text>
                                                </Box>
                                            ) : previewData[file.file_id] ? (
                                                <>
                                                    {/* File Info */}
                                                    <Box bg="gray.50" borderRadius="md" p={4} mb={4}>
                                                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">Filename</Text>
                                                                <Text fontSize="sm" fontWeight="semibold">
                                                                    {file.filename}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">File ID</Text>
                                                                <Text fontSize="sm" fontWeight="semibold">
                                                                    {file.file_id}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">File Size</Text>
                                                                <Text fontSize="sm" fontWeight="semibold">
                                                                    {formatFileSize(file.file_size)}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">Status</Text>
                                                                <Badge
                                                                    colorScheme={
                                                                        file.job_status === 'completed' ? 'green' :
                                                                            file.job_status === 'failed' ? 'red' :
                                                                                file.job_status === 'processing' ? 'yellow' : 'blue'
                                                                    }
                                                                    fontSize="xs"
                                                                >
                                                                    {file.job_status}
                                                                </Badge>
                                                            </Box>
                                                        </Grid>
                                                    </Box>

                                                    {file.job_status === 'completed' && (
                                                        <Box
                                                            bg="green.50"
                                                            borderRadius="lg"
                                                            border="1px solid"
                                                            borderColor="green.100"
                                                            p={4}
                                                            mb={4}
                                                        >
                                                            <Grid templateColumns={{ base: '1fr', lg: '1fr auto' }} gap={4} alignItems="center">
                                                                <Box>
                                                                    <Text fontSize="xs" fontWeight="bold" color="green.700" textTransform="uppercase" mb={2}>
                                                                        Next actions
                                                                    </Text>
                                                                    <Text fontSize="sm" color="gray.700">
                                                                        This dataset has completed processing. You can move from validation into customer assignment,
                                                                        dashboards, and exports without leaving the workspace.
                                                                    </Text>
                                                                </Box>
                                                                <HStack spacing={3} flexWrap="wrap">
                                                                    <Button size="sm" colorScheme="blue" onClick={() => navigate('/settings/manageCustomers')}>
                                                                        <Icon as={FiUsers} mr={2} />
                                                                        Assign customers
                                                                    </Button>
                                                                    <Button size="sm" variant="outline" colorScheme="blue" onClick={() => navigate('/dashboard')}>
                                                                        <Icon as={FiBarChart2} mr={2} />
                                                                        View dashboards
                                                                    </Button>
                                                                    <Button size="sm" variant="outline" colorScheme="blue" onClick={() => navigate('/reportsExports')}>
                                                                        <Icon as={FiDownload} mr={2} />
                                                                        Export report
                                                                    </Button>
                                                                    <Button size="sm" variant="outline" colorScheme="blue" onClick={() => navigate(hasDatasetUploadAccess ? '/subscription' : '/subscription?trial=pro')}>
                                                                        <Icon as={FiCheckCircle} mr={2} />
                                                                        {hasDatasetUploadAccess ? 'Review plan' : 'Start trial'}
                                                                    </Button>
                                                                </HStack>
                                                            </Grid>
                                                        </Box>
                                                    )}

                                                    {/* Preview Table */}
                                                    <Text fontSize="lg" fontWeight="semibold" mb={4}>
                                                        Preview (first 5 rows)
                                                    </Text>
                                                    <Text fontSize="md" fontWeight="light" mb={4}>
                                                        Need to assign customers to RM? Visit the settings tab!
                                                    </Text>

                                                    <Box overflowX="auto" mb={6}>
                                                        <Table variant="striped" size="sm">
                                                            <Thead>
                                                                <Tr>
                                                                    {previewData[file.file_id].columns_info?.map((col) => (
                                                                        <Th key={col.name} whiteSpace="nowrap" fontSize="xs">
                                                                            {col.name}
                                                                        </Th>
                                                                    ))}
                                                                </Tr>
                                                            </Thead>
                                                            <Tbody>
                                                                {previewData[file.file_id].preview_rows?.map((row, idx) => (
                                                                    <Tr key={idx}>
                                                                        {previewData[file.file_id].columns_info?.map((col) => (
                                                                            <Td key={col.name} whiteSpace="nowrap" fontSize="xs">
                                                                                {row[col.name]?.toString() || '-'}
                                                                            </Td>
                                                                        ))}
                                                                    </Tr>
                                                                ))}
                                                            </Tbody>
                                                        </Table>
                                                    </Box>

                                                    {/* Delete Button */}
                                                    <Button
                                                        colorScheme="red"
                                                        size="md"
                                                        leftIcon={<FiTrash2 />}
                                                        onClick={() => setFilePendingDelete(file.file_id)}
                                                        isLoading={deletingFileId === file.file_id}
                                                    >
                                                        Delete File
                                                    </Button>
                                                </>
                                            ) : (
                                                <Box textAlign="center">
                                                    {/* File Info */}
                                                    <Box bg="gray.50" borderRadius="md" p={4} mb={4}>
                                                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">Filename</Text>
                                                                <Text fontSize="sm" fontWeight="semibold">
                                                                    {file.filename}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">File ID</Text>
                                                                <Text fontSize="sm" fontWeight="semibold">
                                                                    {file.file_id}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">File Size</Text>
                                                                <Text fontSize="sm" fontWeight="semibold">
                                                                    {formatFileSize(file.file_size)}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.600">Status</Text>
                                                                <Badge
                                                                    colorScheme={
                                                                        file.job_status === 'completed' ? 'green' :
                                                                            file.job_status === 'failed' ? 'red' :
                                                                                file.job_status === 'processing' ? 'yellow' : 'blue'
                                                                    }
                                                                    fontSize="xs"
                                                                >
                                                                    {file.job_status}
                                                                </Badge>
                                                            </Box>
                                                        </Grid>
                                                    </Box>
                                                    <EmptyStateCard
                                                        icon={FiFile}
                                                        title="Preview not available for this file"
                                                        description="We could not render sample rows for this upload, but the file metadata and processing status are still available."
                                                        hint="You can keep the file, upload a replacement, or remove it if it was the wrong document."
                                                        borderColor="brand.divider"
                                                        titleColor="gray.700"
                                                        descriptionColor="gray.600"
                                                        hintColor="gray.500"
                                                        iconColor="#0B2545"
                                                        maxW="full"
                                                    >
                                                        <Button
                                                            colorScheme="red"
                                                            size="md"
                                                            leftIcon={<FiTrash2 />}
                                                            onClick={() => setFilePendingDelete(file.file_id)}
                                                            isLoading={deletingFileId === file.file_id}
                                                        >
                                                            Delete File
                                                        </Button>
                                                    </EmptyStateCard>
                                                </Box>
                                            )}
                                        </TabPanel>
                                    ))}

                                    {/* Firm-ready data panel: product catalog, holdings, transactions, evidence, model targets */}
                                    <TabPanel p={6}>
                                        <FirmReadyDatasets />
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </Box>
                    </VStack>
                </Container >
            </Box >
            <Footer />
            <ConfirmDialog
                isOpen={Boolean(filePendingDelete)}
                onClose={() => setFilePendingDelete(null)}
                onConfirm={handleDelete}
                title="Delete Dataset"
                description="Remove this file and data still supplied by it. Newer imports and data with unknown origin are preserved. Customers with linked adviser work or other records require review first."
                confirmLabel="Delete File"
                isLoading={Boolean(deletingFileId)}
            />
        </Box >
    );
};

export default Datasets;
